import className from "classnames/bind";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";

import { SIZES } from "@/shared/constants";

import type { UI_CONTEXT } from "@/analytics/constants";
import { trackUserEvent } from "@/analytics/tracking";

import { DropdownMenu } from "@/components/dropdown-menus/dropdown-menu";
import { DropdownMenuItem } from "@/components/dropdown-menus/dropdown-menu-items/dropdown-menu-item-interface";
import { KINDS as DROPDOWN_KINDS } from "@/components/dropdown-menus/dropdown-menu.constants";

import { Locale } from "@/types/locale";

import EVENTS from "../../analytics/events";
import CloseIcon from "../../assets/icons/close-icon.svg";
import Chip from "../chips/chip";
import { KINDS as CHIP_KINDS } from "../chips/chips.constants";
import styles from "./controlFilters.module.scss";

export interface Props {
    locale: Locale;
    hasOffers: boolean;
    uiContext: UI_CONTEXT;
    searchVersion: string;
    searchTerm: string;
    sortByOptions: Array<DropdownMenuItem>;
    storeCount: number;
    handleOffersClick: () => void;
    handleSortByClick: (sortByValue: string) => void;
}

const cx = className.bind(styles);

export const ControlFilters: React.FC<Props> = (props) => {
    const {
        locale,
        hasOffers,
        handleOffersClick,
        handleSortByClick,
        sortByOptions,
        uiContext,
        searchVersion,
        searchTerm,
        storeCount,
    } = props;

    const { t } = useTranslation();
    const router = useRouter();
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [sortByFilterName, setSortByFilterName] = useState(sortByOptions[0].name);

    useEffect(() => {
        const sortBy = router.query.sort_by;
        if (!sortBy) {
            return;
        }
        const option = sortByOptions.find((option) => option.value === sortBy);
        if (option) {
            setSortByFilterName(option.name);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const trackEvent = (eventName: string, sort?: string) => {
        const eventProps = {
            sort,
            searchVersion: searchVersion,
            searchTerm: searchTerm,
            resultsCount: storeCount,
        };
        trackUserEvent(locale, router.asPath, uiContext, eventName, eventProps);
    };

    const handleClick = () => {
        setIsFilterOpen(!isFilterOpen);
    };

    const handleSortByClickWithTracking = (item: DropdownMenuItem) => {
        trackEvent(EVENTS.SORT_CLICK, item.name);
        setSortByFilterName(item.name);
        setIsFilterOpen(!isFilterOpen);
        handleSortByClick(item.value);
    };

    const handleOffersClickWithTracking = () => {
        trackEvent(EVENTS.FILTER_OFFER_CLICK);
        handleOffersClick();
    };

    const chipsProps = {
        disabled: false,
        size: SIZES.REGULAR,
    };

    const desktopFilterContainerClassName = cx({
        [styles.desktopFilterContainer]: true,
        [styles.desktopFilterContainerActive]: isFilterOpen,
    });

    const mobileFilterContainerClassName = cx({
        [styles.mobileFilterContainer]: true,
        [styles.mobileFilterContainerActive]: isFilterOpen,
    });

    const filterOverlayClassName = cx({
        [styles.filterOverlayActive]: isFilterOpen,
    });

    return (
        <div className={styles.controlHeaderFiltersContainer}>
            <div className={styles.filtersContainer}>
                <Chip
                    {...chipsProps}
                    label={t("store.filterOffersOptionText")}
                    kind={CHIP_KINDS.SINGLE}
                    handleClick={handleOffersClickWithTracking}
                    isActive={hasOffers}
                />
                <div>
                    <Chip
                        {...chipsProps}
                        label={`${t("store.filterSortByText")}: ${sortByFilterName}`}
                        kind={CHIP_KINDS.DROPDOWN}
                        handleClick={handleClick}
                        isFilterOpen={isFilterOpen}
                    />
                    {/* Desktop filter */}
                    <div className={desktopFilterContainerClassName}>
                        <DropdownMenu
                            dropdownMenuItems={sortByOptions}
                            kind={DROPDOWN_KINDS.TEXT}
                            onClick={handleSortByClickWithTracking}
                        />
                    </div>
                    {/* Mobile filter */}
                    <div className={mobileFilterContainerClassName}>
                        <span className={styles.mobileFilterHeader}>Sort results</span>
                        <span className={styles.mobileFiltersCloseIcon} onClick={handleClick}>
                            <CloseIcon />
                        </span>
                        <DropdownMenu
                            dropdownMenuItems={sortByOptions}
                            kind={DROPDOWN_KINDS.TEXT}
                            withBorder={false}
                            onClick={handleSortByClickWithTracking}
                        />
                    </div>
                    <div className={filterOverlayClassName} onClick={handleClick} />
                </div>
            </div>
        </div>
    );
};
